import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Edit, User, MapPin, Phone, Mail, FileText, AlertCircle, Download, Eye, FileDown, LayoutTemplate, CheckCircle, ThumbsUp, ThumbsDown, ArrowRight, Info, ChevronDown, ChevronRight, Home, PenTool, Cloud, ZoomIn, ChevronLeft, ChevronRight as ChevronRightIcon, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Toast from './Toast';
import SurveyFlowProgress from './SurveyFlowProgress';
import BudgetSheetsManager from './BudgetSheetsManager';
import SurveyBudgetWizard from './SurveyBudgetWizard';
import ConfirmModal from './ConfirmModal';
import SketchCanvasViewer from './SketchCanvasViewer';
import surveyService from '../services/surveyService';
import pdfService from '../services/pdfService';
import * as sketchService from '../services/sketchService';
import api from '../services/api';
import { useQueryClient } from '@tanstack/react-query';

const SurveyView = ({ survey, onClose, onEdit }) => {
  const { user } = useAuth();
  const isManagement = ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role) || user?.permissions?.includes('all:full');
  const canEdit = isManagement || user?.role === 'RECEPCAO_PROJETOS';
  const [surveyData, setSurveyData] = useState(survey);
  const isCreator = surveyData?.created_by && user?.id && surveyData?.created_by === user.id;
  const canApprove3D = (isManagement || isCreator) && surveyData?.project_3d_status === 'COMPLETED';
  const canApproveRejectClient = isManagement && surveyData?.status === 'APRESENTADO_CLIENTE';
  const canApproveDirect = isManagement && ['RASCUNHO', 'VISITA_REALIZADA', 'EM_PROJETACAO', 'APRESENTACAO_AGENDADA', 'APRESENTADO_CLIENTE'].includes(surveyData?.status) && surveyData?.status !== 'ORCAMENTO_PRONTO';
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isApprovingRejecting, setIsApprovingRejecting] = useState(false);
  const [isApprovingDirect, setIsApprovingDirect] = useState(false);
  const [toast, setToast] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalNewStatus, setStatusModalNewStatus] = useState(null);
  const [managerMargin, setManagerMargin] = useState(0);
  const [managerMarginNotes, setManagerMarginNotes] = useState('');
  const [isSavingMargin, setIsSavingMargin] = useState(false);
  const [isGeneratingBudget, setIsGeneratingBudget] = useState(false);
  const [isApprovingBudget, setIsApprovingBudget] = useState(false);
  const [showBudgetApprovalModal, setShowBudgetApprovalModal] = useState(false);
  const [budgetApprovalData, setBudgetApprovalData] = useState({ approved: true, notes: '' });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [pendingDeleteFile, setPendingDeleteFile] = useState(null);
  const canGenerateBudget = user.role === 'GERENCIA' && ['EM_PROJETACAO', 'EM_REVISAO', 'APRESENTADO_CLIENTE'].includes(survey.status);
  const canApproveBudget = user && ['RECEPCAO_PROJETOS', 'GERENCIA'].includes(user.role) && surveyData && ['ORCAMENTO_PRONTO'].includes(surveyData.status);
  const canAnalyzeMargin = user && user.role === 'GERENCIA' && surveyData && surveyData.status === 'APRESENTADO_CLIENTE' && !['ORCAMENTO_PRONTO', 'ORCAMENTO_REPROVADO', 'ORCAMENTO_APROVADO'].includes(surveyData.status);
  const [showBudgetSheetsModal, setShowBudgetSheetsModal] = useState(false);
  const [showBudgetWizard, setShowBudgetWizard] = useState(false);
  const [expandedSketch, setExpandedSketch] = useState(null);
  const [sketchProjectData, setSketchProjectData] = useState(null);
  const [selectedSketchPageId, setSelectedSketchPageId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    client: true,
    technical: true,
    materials: true,
    files: true,
    environments: true,
    history: false
  });
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.scrollHeight);
    }
  }, [surveyData, showBudgetSheetsModal]);

  const queryClient = useQueryClient();

  useEffect(() => {
    setSurveyData(survey);
  }, [survey]);

  useEffect(() => {
    if (expandedSketch?.sketch_project_id) {
      sketchService.getProjectById(expandedSketch.sketch_project_id)
        .then(res => {
          const data = res.data?.data || res.data;
          setSketchProjectData(data);
          if (data?.canvas_data?.pages?.length) {
            setSelectedSketchPageId(data.canvas_data.pages[0].id);
          }
        })
        .catch(err => {
          console.error('Erro ao carregar projeto de croqui:', err);
          setSketchProjectData(null);
          setSelectedSketchPageId(null);
        });
    } else {
      setSketchProjectData(null);
      setSelectedSketchPageId(null);
    }
  }, [expandedSketch]);

  const { data: statusPipeline } = useQuery({
    queryKey: ['status-pipeline', 'survey'],
    fn: () => api.get('/status-pipeline/survey').then(res => res.data?.data?.status_config),
    staleTime: 5 * 60 * 1000,
  });

  const getNextStatuses = () => {
    if (!statusPipeline || !survey?.status) return [];
    const currentStep = statusPipeline[survey.status];
    return currentStep?.next || [];
  };

  const ENVIRONMENT_TYPE_LABELS = {
    kitchen: 'Cozinha',
    bedroom: 'Quarto',
    living_room: 'Sala',
    bathroom: 'Banheiro',
    office: 'Escritório',
    garage: 'Garagem',
    laundry: 'Área de Serviço',
    balcony: 'Varanda',
    other: 'Outro'
  };

  const formatStatusName = (status) => {
    const statusNames = {
      'RASCUNHO': 'Rascunho',
      'VISITA_AGENDADA': 'Visita Agendada',
      'VISITA_REALIZADA': 'Visita Realizada',
      'EM_PROJETACAO': 'Em Projetacão',
      'APRESENTACAO_AGENDADA': 'Apresentação Agendada',
      'APRESENTADO_CLIENTE': 'Apresentado ao Cliente',
      'ORCAMENTO_PRONTO': 'Orçamento Pronto',
      'ORCAMENTO_REPROVADO': 'Orçamento Reprovado',
      'ORCAMENTO_APROVADO': 'Orçamento Aprovado',
      'EM_PRODUCAO': 'Em Produção',
      'INSTALACAO_AGENDADA': 'Instalação Agendada',
      'INSTALADO': 'Instalado',
      'CONCLUIDO': 'Concluído'
    };
    return statusNames[status] || status;
  };

  const formatHistoryAction = (action) => {
    const map = { CREATE: 'Criação', UPDATE: 'Atualização', DELETE: 'Exclusão' };
    return map[action] || action;
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === surveyData.status) return;
    setStatusModalNewStatus(newStatus);
    setShowStatusModal(true);
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusModalNewStatus(null);
  };

  const handleStatusModalConfirm = async () => {
    if (!statusModalNewStatus) return;
    setIsChangingStatus(true);
    try {
      await surveyService.updateSurveyStatus(survey.id, statusModalNewStatus);
      setSurveyData(prev => ({ ...prev, status: statusModalNewStatus }));
      setShowStatusModal(false);
      setToast({ message: 'Status atualizado com sucesso!', type: 'success' });
      queryClient.invalidateQueries(['surveys']);
    } catch (error) {
      setToast({ message: `Erro ao atualizar status: ${error.message}`, type: 'error' });
    } finally {
      setIsChangingStatus(false);
    }
  };

  const handleApprove3DProject = async () => {
    setIsApproving(true);
    try {
      await surveyService.approve3DProject(survey.id);
      setToast({ message: 'Projeto 3D aprovado com sucesso!', type: 'success' });
      queryClient.invalidateQueries(['surveys']);
      if (onClose) onClose();
    } catch (error) {
      setToast({ message: `Erro ao aprovar projeto: ${error.message}`, type: 'error' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleApproveRejectByClient = async (action) => {
    setIsApprovingRejecting(true);
    try {
      await surveyService.approveOrRejectByClient(survey.id, action);
      setToast({ message: action === 'approve' ? 'Aprovado pelo cliente!' : 'Rejeitado!', type: 'success' });
      queryClient.invalidateQueries(['surveys']);
      if (onClose) onClose();
    } catch (error) {
      setToast({ message: `Erro: ${error.message}`, type: 'error' });
    } finally {
      setIsApprovingRejecting(false);
    }
  };

  const handleApproveDirect = async () => {
    setIsApprovingDirect(true);
    try {
      await surveyService.approveDirect(survey.id);
      setToast({ message: 'Aprovado diretamente!', type: 'success' });
      queryClient.invalidateQueries(['surveys']);
      if (onClose) onClose();
    } catch (error) {
      setToast({ message: `Erro: ${error.message}`, type: 'error' });
    } finally {
      setIsApprovingDirect(false);
    }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await pdfService.generateSurveyPDF(surveyData, user);
      setToast({ message: 'PDF gerado com sucesso!', type: 'success' });
    } catch (error) {
      setToast({ message: `Erro ao gerar PDF: ${error.message}`, type: 'error' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSaveManagerMargin = async () => {
    setIsSavingMargin(true);
    try {
      await api.put(`/surveys/${survey.id}/manager-margin`, {
        margin: managerMargin,
        notes: managerMarginNotes
      });
      setToast({ message: 'Margem salva com sucesso!', type: 'success' });
    } catch (error) {
      setToast({ message: `Erro ao salvar margem: ${error.message}`, type: 'error' });
    } finally {
      setIsSavingMargin(false);
    }
  };

  const handleGenerateBudget = async () => {
    setIsGeneratingBudget(true);
    try {
      await surveyService.generateBudget(survey.id);
      setToast({ message: 'Orçamento gerado com sucesso!', type: 'success' });
      queryClient.invalidateQueries(['surveys']);
    } catch (error) {
      setToast({ message: `Erro ao gerar orçamento: ${error.message}`, type: 'error' });
    } finally {
      setIsGeneratingBudget(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteFile) return;
    try {
      await surveyService.deleteSurveyFile(pendingDeleteFile.id);
      setToast({ message: 'Arquivo excluído com sucesso!', type: 'success' });
      queryClient.invalidateQueries(['survey-files', survey.id]);
    } catch (error) {
      setToast({ message: `Erro ao excluir arquivo: ${error.message}`, type: 'error' });
    } finally {
      setShowDeleteConfirmModal(false);
      setPendingDeleteFile(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setPendingDeleteFile(null);
  };

  const handleApproveBudget = async () => {
    setIsApprovingBudget(true);
    try {
      await surveyService.approveBudget(survey.id, budgetApprovalData);
      setToast({ message: budgetApprovalData.approved ? 'Orçamento aprovado!' : 'Orçamento rejeitado!', type: 'success' });
      setShowBudgetApprovalModal(false);
      setBudgetApprovalData({ approved: true, notes: '' });
      queryClient.invalidateQueries(['surveys']);
    } catch (error) {
      setToast({ message: `Erro: ${error.response?.data?.message || error.message}`, type: 'error' });
    } finally {
      setIsApprovingBudget(false);
    }
  };

  const contentStyle = {
    maxHeight: `calc(100vh - ${headerHeight + 40}px)`,
    overflowY: 'auto'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        
        {/* Header - Fixed at top */}
        <div ref={headerRef} className="flex-shrink-0 border-b border-gray-200 relative">
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 z-10"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Top Row: Title, Status, Actions */}
          <div className="p-4 flex items-start justify-between gap-4">
            {/* Left: Title and Status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-semibold text-gray-900">
                  Levantamento {surveyService.formatSurveyCode(survey.survey_code)}
                </h2>
                {user.role === 'GERENCIA' ? (
                  <select
                    value={surveyData.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isChangingStatus}
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${surveyService.getStatusColor(surveyData.status)}`}
                  >
                    <option value={surveyData.status}>{surveyService.formatStatus(surveyData.status)}</option>
                    {getNextStatuses().map((nextStatus) => (
                      <option key={nextStatus} value={nextStatus}>{surveyService.formatStatus(nextStatus)}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${surveyService.getStatusColor(survey.status)}`}>
                    {surveyService.formatStatus(survey.status)}
                  </span>
                )}
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${surveyService.getPriorityColor(survey.priority)}`}>
                  {surveyService.formatPriority(survey.priority)}
                </span>
                {statusPipeline && getNextStatuses().length > 0 && (
                  <div className="group relative">
                    <button className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                      <Info className="w-3 h-3" />
                      Próximos passos
                    </button>
                    <div className="absolute left-0 top-full mt-1 z-10 hidden group-hover:block">
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px]">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Transições possíveis:</p>
                        <div className="flex flex-wrap gap-1">
                          {getNextStatuses().map((nextStatus) => (
                            <span key={nextStatus} className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700">
                              {formatStatusName(nextStatus)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Buttons Row - Escondido quando APRESENTADO_CLIENTE (wizard handles) */}
              {surveyData.status !== 'APRESENTADO_CLIENTE' && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {canGenerateBudget && (
                    <button onClick={handleGenerateBudget} disabled={isGeneratingBudget}
                      className="inline-flex px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50">
                      <FileText className="w-3.5 h-3.5 mr-1.5" />
                      {isGeneratingBudget ? 'Gerando...' : 'Gerar Orçamento'}
                    </button>
                  )}
                  {!canGenerateBudget && user.role === 'GERENCIA' && (
                    <span className="inline-flex px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed"
                      title={`Status atual não permite gerar orçamento`}>
                      <FileText className="w-3.5 h-3.5 mr-1.5" />
                      Gerar Orçamento
                    </span>
                  )}
                  {canApproveBudget && (
                    <button onClick={() => setShowBudgetApprovalModal(true)}
                      className="inline-flex px-3 py-1.5 text-xs font-medium rounded-lg bg-green-100 text-green-700 hover:bg-green-200">
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                      Aprovar Orçamento
                    </button>
                  )}
                  
                  <button onClick={handleGeneratePDF} disabled={isGeneratingPDF}
                    className="inline-flex px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                    {isGeneratingPDF ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
                    PDF
                  </button>
                  {canEdit && (
                    <button onClick={onEdit} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-50 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Timeline Progress */}
          <div className="px-4 pb-3">
            <SurveyFlowProgress currentStatus={survey.status} />
          </div>

          {/* Wizard de Encerramento - para GERENCIA quando APRESENTADO_CLIENTE */}
          {user.role === 'GERENCIA' && surveyData.status === 'APRESENTADO_CLIENTE' && (
            <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border-t border-emerald-200">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Apresentação Finalizada
                  </h3>
                  <p className="text-sm text-emerald-700 mt-1">
                    Clique abaixo para encerrar o levantamento e processar o orçamento
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowBudgetSheetsModal(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium"
                  >
                    <FileText className="w-4 h-4" />
                    Fichas de Orçamento
                  </button>
                  <button
                    onClick={() => setShowBudgetWizard(true)}
                    className="px-6 py-3 bg-[rgb(73,148,61)] text-white rounded-lg hover:bg-[rgb(60,120,50)] flex items-center gap-2 font-medium shadow-md"
                  >
                    <ArrowRight className="w-5 h-5" />
                    Encerrar Levantamento
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Manager Analysis Section */}
          {canAnalyzeMargin && surveyData.status !== 'APRESENTADO_CLIENTE' && (
            <div className="p-4 bg-amber-50 border-t border-amber-200">
              <h3 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Análise do Gerente - Margem de Preço
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Margem adicional (%)</label>
                  <div className="flex gap-2">
                    <input type="number" step="0.5" min="0" max="50" value={managerMargin}
                      onChange={(e) => setManagerMargin(e.target.value)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="Ex: 5" />
                    <button onClick={handleSaveManagerMargin} disabled={isSavingMargin}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm">
                      {isSavingMargin ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Observações</label>
                  <textarea rows={1} value={managerMarginNotes}
                    onChange={(e) => setManagerMarginNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="Observações sobre a análise..." />
                </div>
              </div>
              {managerMargin > 0 && (
                <p className="mt-2 text-xs text-amber-800">
                  <strong>Margem atual:</strong> {managerMargin}% aplicada no orçamento
                </p>
              )}
            </div>
          )}

          {/* Action Buttons (Approve/Reject) */}
          <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
            {canApproveRejectClient && surveyData.status !== 'APRESENTADO_CLIENTE' && (
              <>
                <button onClick={() => handleApproveRejectByClient('approve')} disabled={isApprovingRejecting}
                  className="inline-flex px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 items-center gap-2 text-sm font-medium">
                  {isApprovingRejecting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                  Aprovar (Pré Projeto)
                </button>
                <button onClick={() => handleApproveRejectByClient('reject')} disabled={isApprovingRejecting}
                  className="inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 items-center gap-2 text-sm font-medium">
                  <ThumbsDown className="w-4 h-4" />
                  Reprovar
                </button>
              </>
            )}
            {canApproveDirect && !canApproveRejectClient && (
              <button onClick={handleApproveDirect} disabled={isApprovingDirect}
                className="inline-flex px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 items-center gap-2 text-sm font-medium">
                {isApprovingDirect ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Aprovar Direto
              </button>
            )}
            {canApprove3D && (
              <button onClick={handleApprove3DProject} disabled={isApproving}
                className="inline-flex px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 items-center gap-2 text-sm font-medium">
                {isApproving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Aprovar 3D
              </button>
            )}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={contentStyle}>
            
            {/* Client Data */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <button onClick={() => toggleSection('client')}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200">
                <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Dados do Cliente
                </h3>
                {expandedSections.client ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
              {expandedSections.client && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nome</label>
                    <p className="text-gray-900 font-medium">{survey.client_name}</p>
                  </div>
                  {survey.client_phone && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Telefone</label>
                      <p className="text-gray-900 flex items-center gap-1"><Phone className="w-4 h-4" />{survey.client_phone}</p>
                    </div>
                  )}
                  {survey.client_email && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Email</label>
                      <p className="text-gray-900 flex items-center gap-1"><Mail className="w-4 h-4" />{survey.client_email}</p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-500">Endereço</label>
                    <p className="text-gray-900 flex items-start gap-1"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />{survey.client_address}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Technical Info */}
            <div className="bg-blue-50 rounded-lg overflow-hidden">
              <button onClick={() => toggleSection('technical')}
                className="w-full px-4 py-3 flex items-center justify-between bg-blue-100 hover:bg-blue-200">
                <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Levantamento Técnico
                </h3>
                {expandedSections.technical ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
              {expandedSections.technical && (
                <div className="p-4 space-y-3">
                  {survey.environment_description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Descrição do Ambiente</label>
                      <p className="text-gray-900 whitespace-pre-wrap">{survey.environment_description}</p>
                    </div>
                  )}
                  {survey.technical_observations && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Observações Técnicas</label>
                      <p className="text-gray-900 whitespace-pre-wrap">{survey.technical_observations}</p>
                    </div>
                  )}
                  {survey.plumbing_info && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Informações de Encanamento</label>
                      <p className="text-gray-900 whitespace-pre-wrap">{survey.plumbing_info}</p>
                    </div>
                  )}
                  {survey.furniture_purpose && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Utilidade dos Móveis</label>
                      <p className="text-gray-900 whitespace-pre-wrap">{survey.furniture_purpose}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Material Preferences */}
            {survey.material_preferences && Object.keys(survey.material_preferences).length > 0 && (
              (survey.material_preferences.material_type?.length > 0 ||
               survey.material_preferences.hardware_type?.length > 0 ||
               survey.material_preferences.has_glass ||
               survey.material_preferences.door_finish?.length > 0) && (
                <div className="bg-green-50 rounded-lg overflow-hidden border border-green-200">
                  <button onClick={() => toggleSection('materials')}
                    className="w-full px-4 py-3 flex items-center justify-between bg-green-100 hover:bg-green-200">
                    <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                      📊 Preferências de Material
                    </h3>
                    {expandedSections.materials ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  {expandedSections.materials && (
                    <div className="p-4 space-y-3">
                      {survey.material_preferences.material_type?.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Tipo de Material</label>
                          <div className="flex flex-wrap gap-2">
                            {survey.material_preferences.material_type.map((type) => (
                              <span key={type} className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full font-semibold">Tipo {type}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {survey.material_preferences.hardware_type?.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Tipo de Ferragens</label>
                          <div className="flex flex-wrap gap-2">
                            {survey.material_preferences.hardware_type.map((type) => (
                              <span key={type} className="px-3 py-1 text-sm bg-purple-100 text-purple-800 rounded-full font-semibold">Tipo {type}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {survey.material_preferences.has_glass && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Vidro</label>
                          <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full font-semibold">✓ Com vidro</span>
                        </div>
                      )}
                      {survey.material_preferences.door_finish?.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 block mb-1">Acabamento das Portas</label>
                          <div className="flex flex-wrap gap-2">
                            {survey.material_preferences.door_finish.map((finish) => (
                              <span key={finish} className="px-3 py-1 text-sm bg-orange-100 text-orange-800 rounded-full font-semibold">{finish}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            )}

            {/* Environments */}
            {survey.rooms && survey.rooms.length > 0 && (
              <div className="bg-purple-50 rounded-lg overflow-hidden">
                <button onClick={() => toggleSection('environments')}
                  className="w-full px-4 py-3 flex items-center justify-between bg-purple-100 hover:bg-purple-200">
                  <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                    <LayoutTemplate className="w-5 h-5" />
                    Ambientes ({survey.rooms.length})
                  </h3>
                  {expandedSections.environments ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedSections.environments && (
                  <div className="p-4 space-y-4">
                    {/* Resumo dos Ambientes */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-white p-3 rounded-lg shadow-sm border">
                        <div className="text-xs text-gray-500">Ambientes</div>
                        <div className="text-xl font-bold text-blue-600">{survey.rooms.length}</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm border">
                        <div className="text-xs text-gray-500">Croquis 2D</div>
                        <div className="text-xl font-bold text-purple-600">
                          {survey.rooms.filter(r => r.sketch_snapshot_image).length}/{survey.rooms.length}
                        </div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg shadow-sm border border-green-200 col-span-2">
                        <div className="text-xs text-green-600 mb-1">Projeto 3D</div>
                        <div className="text-sm font-medium text-green-700 flex items-center gap-1">
                          {survey.project_3d_status === 'PENDING' && '⏳ Pendente'}
                          {survey.project_3d_status === 'IN_PROGRESS' && '🔄 Em Andamento'}
                          {survey.project_3d_status === 'COMPLETED' && '✅ Concluído'}
                          {survey.project_3d_status === 'REVIEW' && '👁️ Em Revisão'}
                          {survey.project_3d_status === 'APPROVED' && '✅ Aprovado'}
                          {!survey.project_3d_status && '❌ Não iniciado'}
                        </div>
                      </div>
                    </div>

                    {/* Lista de Ambientes Expandida */}
                    <div className="space-y-4">
                      {survey.rooms.map((room) => (
                        <div key={room.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          {/* Header do Ambiente */}
                          <div className="p-4 bg-blue-50 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-100">
                                  <Home className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{room.environment_name}</div>
                                  <div className="text-sm text-gray-500">{ENVIRONMENT_TYPE_LABELS[room.environment_type] || room.environment_type}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  room.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                  room.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {room.status === 'COMPLETED' && '✅ Completo'}
                                  {room.status === 'IN_PROGRESS' && '🔄 Em Progresso'}
                                  {(!room.status || room.status === 'PENDING') && '⏳ Pendente'}
                                </span>
                                <span className={`text-xs font-medium px-2 py-1 rounded ${
                                  room.priority === 'HIGH' ? 'bg-red-100 text-red-700' :
                                  room.priority === 'NORMAL' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {room.priority === 'HIGH' && '🔴 Alta'}
                                  {room.priority === 'NORMAL' && '🔵 Normal'}
                                  {(!room.priority || room.priority === 'LOW') && '⚪ Baixa'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Detalhes do Ambiente */}
                          <div className="p-4">
                            {/* Dimensões */}
                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                              <div className="text-xs text-gray-500 mb-1 font-medium">Dimensões</div>
                              <div className="flex gap-6 text-sm">
                                <div><span className="text-gray-600">Largura:</span> <span className="font-medium">{room.dimensions?.width || '-'}</span></div>
                                <div><span className="text-gray-600">Altura:</span> <span className="font-medium">{room.dimensions?.height || '-'}</span></div>
                                <div><span className="text-gray-600">Comprimento:</span> <span className="font-medium">{room.dimensions?.length || '-'}</span></div>
                              </div>
                            </div>

                            {/* Características */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                              {room.wall_type && (
                                <div className="p-2 bg-gray-50 rounded">
                                  <div className="text-xs text-gray-500">Parede</div>
                                  <div className="text-sm font-medium text-gray-900">{room.wall_type}</div>
                                </div>
                              )}
                              {room.floor_type && (
                                <div className="p-2 bg-gray-50 rounded">
                                  <div className="text-xs text-gray-500">Piso</div>
                                  <div className="text-sm font-medium text-gray-900">{room.floor_type}</div>
                                </div>
                              )}
                              {room.ceiling_type && (
                                <div className="p-2 bg-gray-50 rounded">
                                  <div className="text-xs text-gray-500">Teto</div>
                                  <div className="text-sm font-medium text-gray-900">{room.ceiling_type}</div>
                                </div>
                              )}
                            </div>

                            {/* Observações Técnicas */}
                            {room.technical_notes && (
                              <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="text-xs text-yellow-700 font-medium mb-1">Observações Técnicas</div>
                                <div className="text-sm text-gray-700">{room.technical_notes}</div>
                              </div>
                            )}

                            {/* Croqui 2D */}
                            {room.sketch_snapshot_image && (
                              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="text-xs text-purple-700 font-medium mb-2 flex items-center justify-between">
                                  <span className="flex items-center gap-1">
                                    <PenTool className="w-3 h-3" /> Croqui 2D
                                  </span>
                                  <button
                                    onClick={() => setExpandedSketch(room)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 rounded text-purple-700"
                                  >
                                    <ZoomIn className="w-3 h-3" /> Expandir
                                  </button>
                                </div>
                                <img
                                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${room.sketch_snapshot_image}`}
                                  alt={`Croqui ${room.environment_name}`}
                                  className="w-full max-h-48 object-contain rounded border border-purple-300 bg-white cursor-pointer hover:opacity-80"
                                  onClick={() => setExpandedSketch(room)}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}

                            {/* ID do Croqui */}
                            {room.sketch_project_id && (
                              <div className="text-xs text-purple-600">
                                <strong>ID Croqui:</strong> {room.sketch_project_id}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Arquivos do Levantamento */}
            {survey.files?.length > 0 && (
              <div className="bg-green-50 rounded-lg overflow-hidden">
                <button onClick={() => toggleSection('files')}
                  className="w-full px-4 py-3 flex items-center justify-between bg-green-100 hover:bg-green-200">
                  <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Arquivos ({survey.files?.length})
                  </h3>
                  {expandedSections.files ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                {expandedSections.files && (
                  <div className="p-4 space-y-4">
                    {/* Arquivos Gerais */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">📁 Arquivos do Levantamento</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {survey.files.map((file) => (
                            <div key={file.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                              <div className="flex-shrink-0">
                                {file.is_cloudinary_file ? (
                                  <Cloud className="w-6 h-6 text-blue-500" />
                                ) : (
                                  <FileText className="w-6 h-6 text-gray-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.original_name}</p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span>{surveyService.formatFileSize(file.file_size)}</span>
                                  <span>•</span>
                                  <span className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded">{file.file_type}</span>
                                  {file.is_cloudinary_file && (
                                    <span className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded flex items-center gap-1">
                                      <Cloud className="w-3 h-3" /> Cloudinary
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    if (file.cloudinary_url) {
                                      window.open(file.cloudinary_url, '_blank');
                                    } else if (file.drive_web_view_link) {
                                      window.open(file.drive_web_view_link, '_blank');
                                    } else {
                                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '').replace(/\/$/, '');
                                      window.open(`${baseUrl}/api/surveys/files/${file.id}/download`, '_blank');
                                    }
                                  }}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Abrir"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '').replace(/\/$/, '');
                                    const token = localStorage.getItem('accessToken');
                                    window.location.href = `${baseUrl}/api/surveys/files/${file.id}/download?token=${token}`;
                                  }}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Baixar"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Arquivos 3D */}
                    {survey.files_3d?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">📂 Arquivos do Projeto 3D</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {survey.files_3d.map((file) => (
                            <div key={file.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                              <div className="flex-shrink-0">
                                {file.is_cloudinary_file ? (
                                  <Cloud className="w-6 h-6 text-purple-500" />
                                ) : (
                                  <FileText className="w-6 h-6 text-purple-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{file.original_name}</p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span>{surveyService.formatFileSize(file.file_size)}</span>
                                  <span>•</span>
                                  <span className="px-1 py-0.5 bg-purple-100 text-purple-700 rounded">{file.category}</span>
                                  {file.is_cloudinary_file && (
                                    <span className="px-1 py-0.5 bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                                      <Cloud className="w-3 h-3" /> Cloudinary
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    if (file.cloudinary_url) {
                                      window.open(file.cloudinary_url, '_blank');
                                    } else {
                                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '').replace(/\/$/, '');
                                      window.open(`${baseUrl}/api/surveys/files/${file.id}/download`, '_blank');
                                    }
                                  }}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Abrir"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '').replace(/\/$/, '');
                                    const token = localStorage.getItem('accessToken');
                                    window.location.href = `${baseUrl}/api/surveys/files/${file.id}/download?token=${token}`;
                                  }}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="Baixar"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* System Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Informações do Sistema</h3>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                <div><span className="font-medium">Criado por:</span> {survey.created_by_name || 'N/A'}</div>
                <div><span className="font-medium">Criado em:</span> {surveyService.formatDateTime(survey.created_at)}</div>
                <div><span className="font-medium">Atualizado por:</span> {survey.updated_by_name || 'N/A'}</div>
                <div><span className="font-medium">Atualizado em:</span> {surveyService.formatDateTime(survey.updated_at)}</div>
              </div>
            </div>

            {/* History */}
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <button onClick={() => toggleSection('history')}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200">
                <h3 className="text-base font-medium text-gray-900">Histórico de Alterações</h3>
                {expandedSections.history ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
              {expandedSections.history && survey.history && survey.history.length > 0 && (
                <div className="p-4 space-y-2 max-h-60 overflow-y-auto">
                  {survey.history.map((h, i) => (
                    <div key={i} className="text-sm p-2 bg-white rounded border">
                      <div className="flex justify-between">
                        <span className="font-medium">{formatHistoryAction(h.action)}</span>
                        <span className="text-gray-500 text-xs">{surveyService.formatDateTime(h.created_at)}</span>
                      </div>
                      <p className="text-gray-600 text-xs mt-1">{h.user_name} • <span className="font-medium">{h.field_name}:</span> {String(h.new_value || '').substring(0, 100)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        {/* Status Change Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-2">Confirmar Mudança de Status</h3>
              <p className="text-gray-600 mb-4">
                Deseja alterar o status de <strong>{surveyService.formatStatus(surveyData.status)}</strong> para <strong>{surveyService.formatStatus(statusModalNewStatus)}</strong>?
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={handleStatusModalClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={handleStatusModalConfirm} disabled={isChangingStatus}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg disabled:opacity-50">
                  {isChangingStatus ? 'Salvando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Budget Approval Modal */}
        {showBudgetApprovalModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">Aprovação do Orçamento</h3>
                <p className="text-sm text-gray-500">O cliente aprovou o orçamento via WhatsApp?</p>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex gap-2">
                  <button onClick={() => setBudgetApprovalData({ ...budgetApprovalData, approved: true })}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium ${budgetApprovalData.approved ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    Sim, Aprovar
                  </button>
                  <button onClick={() => setBudgetApprovalData({ ...budgetApprovalData, approved: false })}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium ${!budgetApprovalData.approved ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    Não, Rejeitar
                  </button>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 block mb-1">Observações</label>
                  <textarea rows={2} value={budgetApprovalData.notes}
                    onChange={(e) => setBudgetApprovalData({ ...budgetApprovalData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Ex: Cliente aprovou via WhatsApp em 16/04/2026" />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <button onClick={() => { setShowBudgetApprovalModal(false); setBudgetApprovalData({ approved: true, notes: '' }); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={handleApproveBudget} disabled={isApprovingBudget}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {isApprovingBudget ? 'Salvando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Budget Sheets Modal */}
        {showBudgetSheetsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-purple-50">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Fichas de Orçamento - {survey.survey_number || survey.id}
                </h3>
                <button onClick={() => setShowBudgetSheetsModal(false)} className="text-2xl leading-none">&times;</button>
              </div>
              <div className="flex-1 overflow-hidden">
                <BudgetSheetsManager survey={survey} />
              </div>
            </div>
          </div>
        )}

        {/* Budget Wizard Modal */}
        {showBudgetWizard && (
          <SurveyBudgetWizard
            survey={survey}
            onClose={() => setShowBudgetWizard(false)}
            onComplete={() => {
              queryClient.invalidateQueries(['surveys']);
              setShowBudgetWizard(false);
            }}
          />
        )}

        {/* Expanded Sketch Viewer Modal */}
        {expandedSketch && (() => {
          const allSketches = (survey.environments || []).filter(e => e.sketch_snapshot_image);
          const currentIndex = allSketches.findIndex(e => e.id === expandedSketch.id);
          const hasPrev = currentIndex > 0;
          const hasNext = currentIndex < allSketches.length - 1;
          const navigate = (dir) => {
            const newIndex = dir === 'prev' ? currentIndex - 1 : currentIndex + 1;
            if (newIndex >= 0 && newIndex < allSketches.length) {
              setExpandedSketch(allSketches[newIndex]);
            }
          };

          const sketchPages = sketchProjectData?.canvas_data?.pages || [];
          const showPageNav = sketchPages.length > 1;
          const pageNames = sketchPages.map(p => ({ id: p.id, name: p.name }));
          const currentPage = sketchPages.find(p => p.id === selectedSketchPageId) || sketchPages[0];
          const pageIndex = sketchPages.findIndex(p => p.id === selectedSketchPageId);

          return (
            <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-purple-50">
                  <div className="flex items-center gap-3">
                    <PenTool className="w-5 h-5 text-purple-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">Croqui 2D - {expandedSketch.environment_name}</h3>
                      <p className="text-xs text-gray-500">ID: {expandedSketch.sketch_project_id} • {currentIndex + 1}/{allSketches.length}</p>
                    </div>
                  </div>
                  <button onClick={() => setExpandedSketch(null)} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
                </div>

                {/* Page Navigation (if multiple pages) */}
                {showPageNav && (
                  <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 flex items-center gap-4">
                    <span className="text-xs text-purple-700 font-medium">Página:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const idx = sketchPages.findIndex(p => p.id === selectedSketchPageId);
                          if (idx > 0) setSelectedSketchPageId(sketchPages[idx - 1].id);
                        }}
                        disabled={pageIndex <= 0}
                        className="p-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-30"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <select
                        value={selectedSketchPageId}
                        onChange={(e) => setSelectedSketchPageId(e.target.value)}
                        className="text-xs border rounded px-2 py-1"
                      >
                        {pageNames.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          const idx = sketchPages.findIndex(p => p.id === selectedSketchPageId);
                          if (idx < sketchPages.length - 1) setSelectedSketchPageId(sketchPages[idx + 1].id);
                        }}
                        disabled={pageIndex >= sketchPages.length - 1}
                        className="p-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-30"
                      >
                        <ChevronRightIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <span className="text-xs text-purple-600">{pageIndex + 1} de {sketchPages.length}</span>
                  </div>
                )}

                {/* Canvas Viewer */}
                <div className="flex-1 overflow-auto p-4 bg-gray-100 flex items-center justify-center relative">
                  {hasPrev && !showPageNav && (
                    <button onClick={() => navigate('prev')} className="absolute left-4 p-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-md z-10">
                      <ChevronLeft className="w-6 h-6 text-gray-700" />
                    </button>
                  )}
                  {currentPage ? (
                    <SketchCanvasViewer
                      page={currentPage}
                      width={1200}
                      height={800}
                    />
                  ) : expandedSketch.sketch_snapshot_image ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${expandedSketch.sketch_snapshot_image}`}
                      alt={`Croqui ${expandedSketch.environment_name}`}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                      style={{ maxHeight: 'calc(100vh - 280px)' }}
                    />
                  ) : (
                    <div className="text-gray-500 text-center py-20">
                      <PenTool className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>Nenhum conteúdo disponível para esta página</p>
                    </div>
                  )}
                  {hasNext && !showPageNav && (
                    <button onClick={() => navigate('next')} className="absolute right-4 p-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-md z-10">
                      <ChevronRightIcon className="w-6 h-6 text-gray-700" />
                    </button>
                  )}
                </div>

                {/* Footer with actions */}
                <div className="p-4 border-t border-gray-200 bg-white flex items-center justify-between">
                  <div className="flex gap-2">
                    {expandedSketch.sketch_project_id && (
                      <a
                        href={`/croquis/${expandedSketch.sketch_project_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        <PenTool className="w-4 h-4" />
                        Editar no Editor
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedSketch(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Delete File Confirmation Modal */}
        <ConfirmModal
          show={showDeleteConfirmModal}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Excluir Arquivo"
          message={`Tem certeza que deseja excluir o arquivo "${pendingDeleteFile?.original_name || ''}"?`}
          confirmText="Excluir"
          cancelText="Cancelar"
          type="danger"
        />
      </div>
    </div>
  );
};

export default SurveyView;
